import { FormApi } from "final-form";
import React from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { RootState, useDispatch, useSelector } from "src/store";
import { Modal } from "src/ui/shared/Modal/Modal";

import { deleteGroup } from "@dashboard/devices/store/index";
import { Group } from "@dashboard/devices/types/index";
import { useCurrentWorkspaceFiles } from "@dashboard/files/api/helpers";
import { useLicenses } from "@dashboard/licenses/api/helpers";
import { selectProductsByGroupId } from "@dashboard/products/store/selectors/products";
import { List, ListItem, Typography } from "@mui/material";
import { CustomAlert } from "@shared/CustomAlert/CustomAlert";
import { DangerButton } from "@shared/CustomButton";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

type FormValues = {
    name: string;
    description: string;
};
type Props = {
    group: Group;
    open: boolean;
    onClose: () => void;
};

const DeleteGroupDialog = (props: Props) => {
    const { t } = useTranslation(["modals", "common"]);
    const dispatch = useDispatch();

    const { data } = useCurrentWorkspaceFiles({ groups: [props.group.id], includeParentFiles: false }, { skip: !props.open });

    const groupProducts = useSelector((state: RootState) => selectProductsByGroupId(state, props.group.id));

    const licensesLength = props.group.licenseIds.length;
    const productsLength = groupProducts.length;
    const filesLength = data?.count || 0;
    const isGroupEmpty = !licensesLength && !productsLength && !filesLength;

    const licenses = useLicenses();
    const isUniversalGroup = licenses.some((license) => license.universalGroupName === props.group.name);

    const canBeDeleted = isGroupEmpty && !isUniversalGroup;

    const onSubmit = async () => {
        await dispatch(deleteGroup({ id: props.group.id }))
            .unwrap()
            .then(() => showSuccessToast(t("modals:deleteGroup.toast.groupDeletedSuccess")))
            .catch(({ message = t("modals:deleteGroup.toast.groupDeletedError") }) => {
                showErrorToast(message);
            })
            .finally(() => {
                props.onClose();
            });
    };
    const onKeyDown = async (e: React.KeyboardEvent<HTMLDivElement>, form: FormApi<FormValues>) => {
        if (e.key === "Enter" && isGroupEmpty) {
            await form.submit();
        }
    };

    return (
        <Form<FormValues>
            onSubmit={onSubmit}
            render={({ handleSubmit, form }) => (
                <form onSubmit={handleSubmit}>
                    <Modal
                        open={props.open}
                        title={t("modals:deleteGroup.title")}
                        onClose={props.onClose}
                        onKeyDown={(e) => onKeyDown(e, form)}
                        endButton={
                            <DangerButton onClick={form.submit} disabled={!canBeDeleted}>
                                {t("common:button.delete")}
                            </DangerButton>
                        }
                    >
                        {canBeDeleted ? <Typography>{t("modals:deleteGroup.description")}</Typography> : null}

                        {!canBeDeleted && isUniversalGroup ? (
                            <CustomAlert severity="warning">
                                <Typography>{t("modals:deleteGroup.cannotDeleteUniversalLicenses")}</Typography>
                            </CustomAlert>
                        ) : null}

                        {!canBeDeleted && !isGroupEmpty ? (
                            <CustomAlert severity="warning">
                                <Typography>{t("modals:deleteGroup.cannotDeleteReason")}</Typography>

                                <List sx={{ listStyleType: "disc", pl: 4 }}>
                                    <ListItem sx={{ display: "list-item" }}>
                                        <Typography>{t("modals:deleteGroup.licenseCount", { count: licensesLength })}</Typography>
                                    </ListItem>
                                    <ListItem sx={{ display: "list-item" }}>
                                        <Typography>{t("modals:deleteGroup.productCount", { count: productsLength })}</Typography>
                                    </ListItem>
                                    <ListItem sx={{ display: "list-item" }}>
                                        <Typography>{t("modals:deleteGroup.fileCount", { count: filesLength })}</Typography>
                                    </ListItem>
                                </List>
                            </CustomAlert>
                        ) : null}
                    </Modal>
                </form>
            )}
        />
    );
};

export default DeleteGroupDialog;
